import React, { useState, useEffect, useRef } from 'react';

/**
 * ResponsiveLayout - A component that automatically stretches to fill available space
 * with support for fullscreen mode and responsive sizing
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the layout
 * @param {boolean} props.initialFullscreen - Whether to start in fullscreen mode
 * @param {string} props.minHeight - Minimum height of the layout
 * @param {string} props.className - Additional CSS class names
 */
const ResponsiveLayout = ({
  children,
  initialFullscreen = false,
  minHeight = '500px',
  className = ''
}) => {
  const [isFullscreen, setIsFullscreen] = useState(initialFullscreen);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch(err => console.error('Error exiting fullscreen:', err));
    } else if (containerRef.current) {
      containerRef.current.requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(err => console.error('Error entering fullscreen:', err));
    }
  };

  // Handle fullscreen change event
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Setup resize observer to track container dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize with current dimensions
    setDimensions({
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight
    });

    // Set up resize observer
    resizeObserverRef.current = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });

    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Handle window resize events
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        document.exitFullscreen()
          .then(() => setIsFullscreen(false))
          .catch(err => console.error('Error exiting fullscreen:', err));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div
      ref={containerRef}
      className={`
        relative overflow-hidden transition-all duration-200 flex flex-col
        ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'h-full w-full'}
        ${className}
      `}
      style={{
        minHeight: isFullscreen ? '100vh' : minHeight,
        height: isFullscreen ? '100vh' : '100%'
      }}
    >
      {/* Fullscreen toggle button */}
      <button
        type="button"
        onClick={toggleFullscreen}
        className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 z-10"
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
          </svg>
        )}
      </button>

      {/* Size indicator (only visible in development) - moved to bottom right with reduced opacity */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-gray-800 text-white text-xs rounded opacity-25 hover:opacity-60 transition-opacity z-10 text-[9px]">
          {dimensions.width}×{dimensions.height}
        </div>
      )}

      {/* Content container */}
      <div className="w-full h-full overflow-hidden">
        {children}
      </div>
    </div>
  );
};

export default ResponsiveLayout;